import { Location } from '@angular/common';
import '@angular/common/locales/global/da';
import '@angular/common/locales/global/de';
import '@angular/common/locales/global/en';
import '@angular/common/locales/global/no';
import '@angular/common/locales/global/sv';
import { DEFAULT_CURRENCY_CODE, Inject, InjectionToken, NgModule } from '@angular/core';
import { BrowserModule, HAMMER_GESTURE_CONFIG, HammerModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxImageZoomModule } from 'ngx-image-zoom';
import { ReplaySubject, Subject } from 'rxjs';
import { ChromeModule as BusinessChromeModule, ChromeModule } from 'src/business/chrome/chrome.module';
import { CommerceHeaderComponent } from 'src/business/chrome/commerce-header/commerce-header.component';
import { FooterComponent } from 'src/business/chrome/footer/footer.component';
import { HeaderComponent } from 'src/business/chrome/header/header.component';
import { CustomizerRequest, CustomizerResponse } from 'src/business/customizer/model';
import { CUSTOMIZER, CustomizerWorkerSubject } from 'src/business/customizer/token';
import { instanceOf } from 'src/business/guards/model.guard';
import { MapToGA4 } from 'src/business/Mapper/map-to-ga4-item';
import { MarketSegmentSelectorComponent } from 'src/business/market-segment-selector/market-segment-selector.component';
import { LineItemReturnModel as LineItem, ProductCardModel, ProductModel } from 'src/business/services/client.generated';
import { BASE_URL, ENVIROMENT, LANGUAGE, POP_STATE } from 'src/core-lib/misselaineous/boot.tokens';
import { InitialLoads } from 'src/core-lib/misselaineous/initial-loads';
import { ContentCache } from 'src/core-lib/navigation/services/content-cache.service';
import { Price } from 'src/core-lib/pipes/models/price-transformers';
import { PipeModule } from 'src/core-lib/pipes/pipe.module';
import { PRICE_MAP_FN } from 'src/core-lib/pipes/tokens';
import { CtaComponent } from 'src/cta/cta.component';
import { environment } from 'src/environments/environment';
import { CONSENT } from 'src/externals/consent/tokens';
import { GOOGLE_MAPS_API_KEY } from 'src/externals/google-maps/tokens';
import { TagManagerService } from 'src/externals/tag-manager/services/tag-manager.service';
import { TagManagerModule } from 'src/externals/tag-manager/tag-manager.module';
import { GA4_ITEMS_MAPPER } from 'src/externals/tag-manager/tokens';
import { FabModule } from 'src/layout/fab/fab.module';
import { GridModule } from 'src/layout/grid/grid.module';
import { MediaModule } from 'src/layout/media/media.module';
import { OverlayModule } from 'src/layout/overlay/overlay.module';
import { PaginationModule } from 'src/layout/pagination/pagination.module';
import { AppComponent } from './app.component';
import { config, MyHammerConfig } from './config';
import { gridElements } from './grid.elements';
import { NotificationModel } from './notification/models/notification';
import { NotificationModule } from './notification/notification.module';
import { NOTIFICATION, NOTIFICATION_FN } from './notification/tokens';
import { RouteConfig } from './routes';
import { URL_UPDATE } from './tokens';



// setup required global languages here for the solution



@NgModule({
    declarations: [
        AppComponent,
    ],
    imports: [
        ChromeModule,
        BrowserModule,
        OverlayModule,
        BusinessChromeModule,
        HammerModule,
        MediaModule.forRoot(config.svgDef),
        TranslateModule.forRoot(config.translate),
        TagManagerModule,
        RouterModule.forRoot(RouteConfig.routes(), RouteConfig.routerOptions),
        GridModule.add({ elements: gridElements }),


        BrowserAnimationsModule,
        FabModule,
        PaginationModule,
        NgxImageZoomModule,
        PipeModule,
        NotificationModule,
        HeaderComponent,
        CommerceHeaderComponent,
        FooterComponent,
        MarketSegmentSelectorComponent,
        CtaComponent,
    ],
    providers: [
        {
            provide: CUSTOMIZER,
            useFactory: (
                notificationService: ReplaySubject<NotificationModel>,
                _baseUrl: string,
                _catalog: string,
            ) =>
                new CustomizerWorkerSubject<CustomizerRequest, CustomizerResponse>(
                    notificationService,
                    _baseUrl,
                    _catalog
                ),
            deps: [NOTIFICATION, BASE_URL, LANGUAGE]
        },
        {
            provide: URL_UPDATE,
            useFactory: (_location: Location) => {
                var update = new Subject<string>();
                _location.onUrlChange((url: string, state: unknown) => update.next(url));
                return update;
            },
            deps: [Location]
        },
        {
            provide: POP_STATE,
            useFactory: (router) => InitialLoads.popState(router),
            deps: [
                Router
            ]
        },
        {
            provide: GOOGLE_MAPS_API_KEY,
            useValue: '',
        },
        {
            provide: HAMMER_GESTURE_CONFIG,
            useClass: MyHammerConfig
        },
        {
            provide: ENVIROMENT,
            useFactory: (cc: ContentCache) => cc.get<string>('environment'),
            deps: [ContentCache]
        },
        { provide: DEFAULT_CURRENCY_CODE, useValue: 'DKK' },

        {
            provide: CONSENT,
            useValue: environment.consent,
        },
        {
            provide: NOTIFICATION,
            useValue: NOTIFICATION_FN()
        },
        {
            provide: GA4_ITEMS_MAPPER,
            useValue: (items: Array<LineItem | ProductCardModel | ProductModel>) => {
                if (items == null || !Array.isArray(items))
                    return [];
                return items.map(item => {

                    if (instanceOf.lineItem(item))
                        return MapToGA4.lineItemReturnModel(item as LineItem);
                    else if (instanceOf.productCardModel(item))
                        return MapToGA4.productCardModel(item as ProductCardModel);
                    else if (instanceOf.productModel(item))
                        return MapToGA4.productModel(item as ProductModel);
                    else {
                        console.error(item);
                        throw new Error("Not Instance of LineItem, ProductCardModel or ProductModel");
                    }

                }).reduce((acc, curr) => acc.concat(curr), [])
            }
        }
    ],
    bootstrap: [AppComponent]
})
export class AppModule {

    constructor(
        @Inject(LANGUAGE) lang: string,
        translateService: TranslateService,
        tagManagerService: TagManagerService,
    ) {
        translateService.use(lang);
    }
}
